import React, { createContext, useContext, useState, ReactNode } from "react";

export interface AuthData {
	email: string;
	phone: string;
	password: string;
	firstName: string;
	lastName: string;
	dateOfBirth: string;
	nationality: string;
	germanLevel: string;
	extraId?: number;
	phoneNumber?: string;
	authMethod?: "email" | "google";
	googleProfile?: {
		name: string;
		email: string;
		picture?: string;
	};
}

export interface AuthContextType {
	currentStep: number;
	setCurrentStep: (step: number) => void;
	authData: AuthData;
	updateAuthData: (data: Partial<AuthData>) => void;
	resetAuth: () => void;
	handleGoogleAuth: (profile: any, isSignIn?: boolean) => Promise<void>;
	checkUserExists: (email: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const initialAuthData: AuthData = {
	email: "",
	phone: "",
	password: "",
	firstName: "",
	lastName: "",
	dateOfBirth: "",
	nationality: "",
	germanLevel: "",
	authMethod: "email",
};

export function AuthProvider({ children }: { children: ReactNode }) {
	// Default landing page - Start with Sign In (step 0)
	const [currentStep, setCurrentStep] = useState(0);
	const [authData, setAuthData] = useState<AuthData>(initialAuthData);

	const updateAuthData = (data: Partial<AuthData>) => {
		setAuthData((prev) => ({ ...prev, ...data }));
	};

	const resetAuth = () => {
		setAuthData(initialAuthData);
		setCurrentStep(0); // Reset to Sign In
	};

	// Mock user database for demonstration
	const mockUserDatabase = [
		"existing.user@gmail.com",
		"john.doe@gmail.com",
		"jane.smith@gmail.com",
	];

	const checkUserExists = async (email: string): Promise<boolean> => {
		// Simulate API call delay
		await new Promise((resolve) => setTimeout(resolve, 500));

		// Check if user exists in mock database
		return mockUserDatabase.includes(email.toLowerCase());
	};

	const handleGoogleAuth = async (profile: any, isSignIn: boolean = false) => {
		try {
			// Extract data from Google profile
			const { name, email, picture } = profile;
			const [firstName, ...lastNameParts] = name.split(" ");
			const lastName = lastNameParts.join(" ");

			// Update auth data with Google profile
			const googleAuthData = {
				email,
				firstName,
				lastName,
				authMethod: "google" as const,
				googleProfile: {
					name,
					email,
					picture,
				},
			};

			updateAuthData(googleAuthData);

			if (isSignIn) {
				// For sign-in flow, check if user exists
				const userExists = await checkUserExists(email);

				if (userExists) {
					// Existing user - should bypass to dashboard
					// This will be handled by the SignIn component
					return;
				} else {
					// New user - go to phone number screen
					setCurrentStep(2); // Phone number screen
				}
			} else {
				// For create account flow, always go to phone number
				setCurrentStep(2); // Phone number screen
			}
		} catch (error) {
			console.error("Google authentication error:", error);
			throw error;
		}
	};

	return (
		<AuthContext.Provider
			value={{
				currentStep,
				setCurrentStep,
				authData,
				updateAuthData,
				resetAuth,
				handleGoogleAuth,
				checkUserExists,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
